/**
 * DiagramComposer — orchestrates applying resolved vocabulary tokens to plays.
 *
 * When a coach types a play call like "3 I Z 44 Power", the PlayCallParser
 * resolves each token to a VocabularyEntry. This service applies those entries
 * to the canvas in canonical order: Formation -> Modifiers -> Concepts
 * (motion -> blocking -> routes -> protection).
 *
 * Formation application delegates to mutations.applyFormation (already works).
 * Concept application creates a reference AND resolves assignments into drawings.
 * Modifier application is stubbed for V1 (requires FormationState computation).
 */

import { secureFetch } from '@/utils/SecureFetch'
import {
	applyConceptToPlay,
	validateConceptForFormation,
} from '@/utils/ConceptApplication.utils'
import type { ParsedToken } from './PlayCallParser'
import type { PlayBlockMutations } from '@/hooks/usePlayBlocks/Types'
import type { Player } from '@/types/Player.types'
import type { Drawing } from '@/types/Drawing.types'
import type { Concept } from '@/types/Concept.types'
import type { VocabularyEntry, VocabularyCategory } from '@/db/Types'

/** Canonical application order (lower = applied first) */
const CATEGORY_ORDER: Record<VocabularyCategory, number> = {
	formation: 0,
	modifier: 1,
	motion: 2,
	blocking: 3,
	route: 4,
	protection: 5,
}

export interface CompositionResult {
	appliedTokens: ParsedToken[]
	skippedTokens: ParsedToken[]
	conflicts: ConflictInfo[]
	warnings: string[]
	errors: string[]
}

export interface ConflictInfo {
	playerId: string
	playerLabel: string
	existingToken: string
	newToken: string
	resolution: 'overwrite' | 'skip'
}

function emptyResult(): CompositionResult {
	return {
		appliedTokens: [],
		skippedTokens: [],
		conflicts: [],
		warnings: [],
		errors: [],
	}
}

export class DiagramComposer {
	/**
	 * Apply a single vocabulary entry to the current play.
	 * Called when a coach selects an entry from the search bar.
	 */
	async applyEntry(
		entry: VocabularyEntry,
		mutations: PlayBlockMutations,
		players: Player[],
		drawings: Drawing[],
	): Promise<CompositionResult> {
		const result = emptyResult()

		try {
			if (entry.entity_type === 'formation') {
				console.log(`[DiagramComposer] Applying formation. entity_id=${entry.entity_id}, code=${entry.code}`)
				await mutations.applyFormation(entry.entity_id)
				result.appliedTokens.push(entryToToken(entry))
			} else if (entry.entity_type === 'concept') {
				await this.applyConceptEntry(entry, mutations, players, drawings, result)
			} else if (entry.entity_type === 'modifier') {
				result.warnings.push(`Modifier application not yet supported: ${entry.code}`)
				result.skippedTokens.push(entryToToken(entry))
			}
		} catch (error) {
			result.errors.push(
				`Failed to apply ${entry.code}: ${error instanceof Error ? error.message : 'Unknown error'}`,
			)
		}

		return result
	}

	/**
	 * Compose a full play from a sequence of parsed tokens.
	 * Applies in canonical order: Formation -> Modifiers -> Concepts.
	 */
	async compose(
		tokens: ParsedToken[],
		mutations: PlayBlockMutations,
		players: Player[],
		drawings: Drawing[],
	): Promise<CompositionResult> {
		const result = emptyResult()

		const resolved = tokens.filter(t => t.isResolved)
		const sorted = [...resolved].sort((a, b) => {
			const orderA = a.category ? (CATEGORY_ORDER[a.category] ?? 99) : 99
			const orderB = b.category ? (CATEGORY_ORDER[b.category] ?? 99) : 99
			return orderA - orderB
		})

		// Track evolving drawing state so successive concepts see earlier drawings
		let currentDrawings = [...drawings]

		for (const token of sorted) {
			try {
				if (token.entityType === 'formation' && token.entityId) {
					await mutations.applyFormation(token.entityId)
					result.appliedTokens.push(token)
				} else if (
					token.entityType === 'concept' &&
					token.entityId &&
					token.vocabularyEntry
				) {
					const created = await this.applyConceptEntry(
						token.vocabularyEntry,
						mutations,
						players,
						currentDrawings,
						result,
					)
					if (created) {
						currentDrawings = [...currentDrawings, ...created]
					}
				} else if (token.entityType === 'modifier') {
					result.warnings.push(`Modifier application not yet supported: ${token.text}`)
					result.skippedTokens.push(token)
				}
			} catch (error) {
				result.errors.push(
					`Failed to apply ${token.text}: ${error instanceof Error ? error.message : 'Unknown error'}`,
				)
			}
		}

		// Unresolved tokens
		for (const token of tokens) {
			if (!token.isResolved) {
				result.skippedTokens.push(token)
			}
		}

		return result
	}

	/**
	 * Detect conflicts where multiple concepts target the same player.
	 * Returns conflicts with 'overwrite' resolution (last token wins).
	 *
	 * NOTE: Full conflict detection requires fetching concept assignments.
	 * This implementation detects same-concept duplicates only;
	 * cross-concept player conflicts are reported during composition.
	 */
	detectConflicts(tokens: ParsedToken[], _players: Player[]): ConflictInfo[] {
		const seen = new Map<string, string>()
		const conflicts: ConflictInfo[] = []

		for (const token of tokens) {
			if (token.entityType !== 'concept' || !token.entityId) continue

			const previous = seen.get(token.entityId)
			if (previous) {
				conflicts.push({
					playerId: token.entityId,
					playerLabel: token.text,
					existingToken: previous,
					newToken: token.text,
					resolution: 'overwrite',
				})
			}
			seen.set(token.entityId, token.text)
		}

		return conflicts
	}

	/**
	 * Apply a concept entry: create reference, fetch data, resolve assignments, create drawings.
	 * Returns newly created drawings on success, null on failure.
	 */
	private async applyConceptEntry(
		entry: VocabularyEntry,
		mutations: PlayBlockMutations,
		players: Player[],
		drawings: Drawing[],
		result: CompositionResult,
	): Promise<Drawing[] | null> {
		// Step 1: Create concept reference block
		await mutations.applyConcept(entry.entity_id)

		// Step 2: Fetch concept data with assignments
		const concept = await this.fetchConcept(entry.entity_id)
		if (!concept) {
			result.warnings.push(`Could not load concept data for ${entry.code}`)
			result.appliedTokens.push(entryToToken(entry))
			return null
		}

		// Step 3: Validate concept against current formation
		const validation = validateConceptForFormation(concept, players)
		if (!validation.valid) {
			result.warnings.push(...validation.errors)
		}

		// Step 4: Resolve assignments and build drawings
		const applicationResult = applyConceptToPlay(concept, players, drawings)

		if (!applicationResult.success) {
			result.errors.push(...applicationResult.errors)
		}
		result.warnings.push(...applicationResult.warnings)

		// Step 5: Persist each drawing via mutations
		const created: Drawing[] = []
		for (const drawing of applicationResult.appliedDrawings) {
			try {
				await mutations.addDrawing(drawing)
				created.push(drawing)
			} catch (error) {
				result.errors.push(
					`Failed to create drawing: ${error instanceof Error ? error.message : 'Unknown error'}`,
				)
			}
		}

		result.appliedTokens.push(entryToToken(entry))
		return created
	}

	/**
	 * Fetch a concept's full data (including assignments) from the API.
	 */
	private async fetchConcept(conceptId: string): Promise<Concept | null> {
		try {
			const response = await secureFetch(`/api/concepts/${conceptId}`)
			if (!response.ok) return null
			const data = await response.json()
			return data.concept ?? null
		} catch {
			return null
		}
	}
}

/** Convert a VocabularyEntry to a ParsedToken for result tracking. */
function entryToToken(entry: VocabularyEntry): ParsedToken {
	return {
		text: entry.code,
		vocabularyEntry: entry,
		category: entry.category,
		entityType: entry.entity_type,
		entityId: entry.entity_id,
		isResolved: true,
	}
}
